/* V4: One purchase position per form. Existing Back-in-Stock app remains responsible for its dialog. */
(() => {
  if (window.__mkPurchaseV4) return;
  window.__mkPurchaseV4 = true;
  const instances = new Map();
  const APP = '#sealapps-bis-widget, .email-me-button';

  // Pure decision function, also used by the regression checks.
  function decide(product, values, complete) {
    if (!product.available) return { mode: 'notify' };
    if (!complete) return { mode: 'choose' };
    const variant = product.variants.find((v) => v.options.every((value, i) => String(value) === values[i]));
    if (!variant) return { mode: 'unavailable' };
    return { mode: variant.available ? 'buy' : 'notify', variant };
  }

  function init(info) {
    if (instances.has(info)) return;
    const data = info.querySelector('[data-mk-product]');
    if (!data) return;
    const product = JSON.parse(data.textContent);
    const initialButtons = info.querySelector('buy-buttons');
    const formId = initialButtons?.getAttribute('form');
    const form = document.forms[formId];
    if (!form || !Array.isArray(product.variants) || !product.variants.length) return;
    const abort = new AbortController();
    let disposed = false, pending = false, frame = 0, flashTimer;
    const listen = (target, type, callback, capture = false) => target.addEventListener(type, callback, { signal: abort.signal, capture });
    const picker = () => info.querySelector(`variant-picker[form-id="${CSS.escape(formId)}"]`);
    const inputs = () => [...(picker()?.querySelectorAll('input[data-option-position]') || [])];
    const isMulti = product.variants.length > 1;
    const texts = {
      choose: initialButtons.dataset.buttonText || (document.documentElement.lang === 'de' ? 'Wähle deine Größe' : 'Choose your size'),
      buy: product.buyLabel || (document.documentElement.lang === 'de' ? 'In den Warenkorb' : 'Add to Cart'),
      unavailable: document.documentElement.lang === 'de' ? 'Nicht verfügbar' : 'Unavailable',
      loading: document.documentElement.lang === 'de' ? 'Wird geladen …' : 'Loading …'
    };
    // Preserve the theme's translated/pre-order label from a server-rendered available variant.
    const initialButton = initialButtons.querySelector('button[type="submit"]');
    if (product.variants.length === 1 && product.variants[0].available && initialButton) texts.buy = initialButton.textContent.trim();

    if (isMulti || !product.available) {
      inputs().forEach((input) => { input.checked = false; input.removeAttribute('checked'); });
    }

    function selection() {
      if (!isMulti && product.available) return { values: product.variants[0].options.map(String), complete: true };
      const all = inputs();
      const positions = [...new Set(all.map((input) => Number(input.dataset.optionPosition)))].sort((a, b) => a - b);
      const values = positions.map((position) => {
        const input = all.find((item) => Number(item.dataset.optionPosition) === position && item.checked);
        if (!input) return null;
        const label = input.labels?.[0];
        return input.dataset.mkOptionLabel ?? label?.dataset.value ?? label?.textContent.trim() ?? null;
      });
      return { values, complete: positions.length > 0 && values.every((v) => v !== null) };
    }
    function getState() {
      const selected = selection();
      return decide(product, selected.values, selected.complete);
    }
    function flash() {
      const el = picker()?.querySelector('.variant-picker__option-values');
      if (!el) return;
      el.classList.remove('mk-pulse');
      void el.offsetWidth;
      el.classList.add('mk-pulse');
      clearTimeout(flashTimer);
      flashTimer = setTimeout(() => el.classList.remove('mk-pulse'), 1000);
    }
    function syncAvailability(state) {
      const box = info.querySelector('[data-mk-availability]');
      if (!box) return;
      // Before a deliberate selection, report product availability, never a URL/default variant.
      const available = state.mode === 'choose' ? product.available : state.mode === 'buy';
      const data = info.querySelector('[data-mk-availability-data]');
      const entry = state.variant && data ? JSON.parse(data.textContent)[String(state.variant.id)] : null;
      box.classList.toggle('is-sold-out', !available);
      box.classList.toggle('no-pickup', !available || (entry ? !entry.p : false));
    }
    function update() {
      if (disposed || !info.isConnected) return;
      const buttons = info.querySelector('buy-buttons');
      const button = buttons?.querySelector('button[type="submit"]');
      if (!button) return;
      const state = getState();
      if (info.dataset.mkPurchase !== state.mode) info.dataset.mkPurchase = state.mode;
      syncAvailability(state);
      let slot = info.querySelector('[data-mk-notify-slot]');
      if (!slot) {
        slot = document.createElement('div');
        slot.dataset.mkNotifySlot = '';
        buttons.after(slot);
      }
      const widget = info.querySelector(APP);
      if (widget && !slot.contains(widget)) {
        const block = widget.closest('.product-info__block-item');
        // Move the existing app block, preserving all handlers and the app's form.
        const host = block && block !== buttons.closest('.product-info__block-item') ? block : widget;
        if (!host.contains(buttons) && !host.contains(slot)) slot.appendChild(host);
      }
      const ready = !!slot.querySelector('.email-me-button, #sealapps-bis-widget button, #sealapps-bis-widget a');
      const showNotify = state.mode === 'notify' && ready;
      info.dataset.mkNotifyReady = String(ready);
      if (slot.hidden !== !showNotify) slot.hidden = !showNotify;
      if (buttons.hidden !== showNotify) buttons.hidden = showNotify;
      if (showNotify) return;
      let label = texts.unavailable;
      let disabled = true;
      if (state.mode === 'choose') { label = texts.choose; disabled = false; }
      if (state.mode === 'notify') label = texts.loading;
      if (state.mode === 'buy') {
        const variantTitle = state.variant.title?.trim();

        label = variantTitle && variantTitle !== 'Default Title'
          ? `${variantTitle} · ${texts.buy}`
          : texts.buy;
        disabled = pending;
      }
      // Leave the theme's loading/spinner markup intact during cart operations.
      const holder = button.querySelector('[data-mk-button-label]');
      if (holder) { if (holder.textContent !== label) holder.textContent = label; }
      else {
        const span = document.createElement('span');
        span.dataset.mkButtonLabel = '';
        span.textContent = label;
        button.replaceChildren(span);
      }
      if (button.disabled !== disabled) button.disabled = disabled;
      button.classList.toggle('is-disabled', disabled);
      button.style.removeProperty('opacity');
      button.style.removeProperty('pointer-events');
      button.style.removeProperty('cursor');
      const idInput = form.querySelector('input[name="id"]');
      if (!isMulti && state.mode === 'buy' && idInput) { idInput.disabled = false; idInput.value = state.variant.id; }
    }
    function schedule() {
      if (frame || disposed) return;
      frame = requestAnimationFrame(() => { frame = 0; update(); });
    }
    // Capture before Prestige's delegated handler. Incomplete combinations must not be submitted/rendered.
    listen(info, 'change', (event) => {
      if (!event.target.matches('input[data-option-position]')) return;
      const selected = selection();
      if (!selected.complete) { event.stopPropagation(); pending = false; }
      else pending = true;
      update();
    }, true);
    listen(form, 'variant:change', (event) => {
      pending = false;
      update();
    });
    listen(info, 'click', (event) => {
      const button = event.target.closest('buy-buttons button[type="submit"]');
      if (!button || getState().mode !== 'choose') return;
      event.preventDefault(); event.stopImmediatePropagation(); flash();
    }, true);
    listen(form, 'submit', (event) => {
      if (event.target !== form) return;
      if (getState().mode === 'buy' && !pending) return;
      event.preventDefault(); event.stopImmediatePropagation(); flash();
    }, true);
    // Only child insertion/removal is observed: our class/style changes cannot cause feedback loops.
    const observer = new MutationObserver(schedule);
    observer.observe(info, { childList: true, subtree: true });
    instances.set(info, () => {
      disposed = true; abort.abort(); observer.disconnect();
      cancelAnimationFrame(frame); clearTimeout(flashTimer); instances.delete(info);
    });
    update();
  }
  const start = () => {
    for (const [info, dispose] of instances) if (!info.isConnected) dispose();
    document.querySelectorAll('safe-sticky.product-info').forEach(init);
  };
  document.addEventListener('shopify:section:unload', (event) => {
    for (const [info, dispose] of instances) if (event.target.contains(info)) dispose();
  });
  document.addEventListener('shopify:section:load', start);
  document.addEventListener('product:rerender', () => queueMicrotask(start), true);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
